import { RefObject } from 'react';
import moment from 'moment';
import {
  COMPANY_CFC,
  DEFAULT_PAGE_VIEW,
  DEFAULT_SCROLL_INFO,
  ELECTRIC_SUMMARY_FILTER_TYPE,
  INTERNET_SUMMARY_FILTER_TYPE,
  MOBILE_SUMMARY_FILTER_TYPE,
  SORT_TYPE,
  SummaryFilterType,
} from '../../Common';
import { CooperationInfoList } from '../../external/LinkedDataApi';
import { FilterLinkedDataStateBase } from '../../redux/reducers/FilterLinkedDataReducer';
import { UserState } from '../../redux/reducers/UserReducer';

export const SORT_LABEL_LINKED = {
  CREATE_DATE_DESC: '受付日時が古い順',
  CREATE_DATE_ASC: '受付日時が新しい順',
};
export interface CrewUserState extends UserState {
  combiAgencyCd: string;
  subordinationType?: string;
}

const ELECTRIC_SUMMARY_FILTER_TYPE_94: {
  [key: string]: SummaryFilterType;
} = {
  '94': {
    code: '94',
    label: '電力申込',
    title: 'おうちでんき',
    type: 'homeElectricity',
  },
};

export const SUMMARY_FILTER_TYPES = {
  ...INTERNET_SUMMARY_FILTER_TYPE,
  ...MOBILE_SUMMARY_FILTER_TYPE,
  ...ELECTRIC_SUMMARY_FILTER_TYPE,
  ...ELECTRIC_SUMMARY_FILTER_TYPE_94,
};

export const textBlank = '------';
const POWER_APPLICATION_CODE = {
  SEVENTY_FOUR: '74',
  NINETY_FOUR: '94',
};
// APIのためにデータを変換する / convert data for api
export const convertDataFilterToRequest = (agencyCodes: string[], filterLinkedData: FilterLinkedDataStateBase) => {
  const { fromDate, toDate, order, mobile, internet, electric, from } = filterLinkedData;
  const businessTypes: string[] = [...mobile, ...internet, ...electric];
  if (businessTypes.includes(POWER_APPLICATION_CODE.SEVENTY_FOUR)) {
    businessTypes.push(POWER_APPLICATION_CODE.NINETY_FOUR);
  }
  return {
    agencyCodes,
    sortCodes: [order],
    size: DEFAULT_SCROLL_INFO.SIZE,
    from,
    businessTypes,
    ...(fromDate && { fromDate: moment(fromDate).format('YYYY-MM-DD') }),
    ...(toDate && { toDate: moment(toDate).format('YYYY-MM-DD') }),
  };
};

export const getViewList = (
  searchResult: CooperationInfoList[],
  page: number,
  apiUpdateIndex: number,
  rowPerPage: number = DEFAULT_PAGE_VIEW.RECORD_ROWS
): CooperationInfoList[] => {
  if (!searchResult.length) {
    return searchResult;
  }

  const hasDataList: CooperationInfoList[][] = sliceArray(searchResult, rowPerPage);
  const index: number = calcArrayIndex(page, apiUpdateIndex, rowPerPage);
  return hasDataList[index] || [];
};

export const sliceArray = (array: CooperationInfoList[], rowPerPage: number): CooperationInfoList[][] => {
  return array.reduce(
    (acc: CooperationInfoList[][], _, i: number) => (i % rowPerPage ? acc : [...acc, ...[array.slice(i, i + rowPerPage)]]),
    []
  );
};

export const calcArrayIndex = (page: number, apiUpdateIndex: number, rowPerPage: number): number => {
  // キャッシュしてるページの半分件数:200件20ページ分キャッシュしてるので、10となる
  const halfPageCount = DEFAULT_SCROLL_INFO.SIZE / 2 / rowPerPage;
  // キャッシュしたページ：初期値は11、初期値未満(<11)になったら、初期値で補填を行う。
  const _calcApiUpdateIndex = apiUpdateIndex < DEFAULT_PAGE_VIEW.API_UPDATE_INDEX ? DEFAULT_PAGE_VIEW.API_UPDATE_INDEX : apiUpdateIndex;
  // キャッシュしてるページの開始インデックス(1始まり)
  const startPage = _calcApiUpdateIndex - halfPageCount < 0 ? 0 : _calcApiUpdateIndex - halfPageCount;
  // キャッシュしてるページ配列のインデックス(0始まり)

  return page - startPage;
};

export const formatSendTime = (sendTime: string) => {
  if (!sendTime) return '';
  return moment(sendTime).format('YYYY/MM/DD HH:mm:ss');
};

export const renderTitleSummary = (businessType: string) => {
  return SUMMARY_FILTER_TYPES[businessType]?.title || textBlank;
};

export const renderLabelSummary = (businessType: string) => {
  return SUMMARY_FILTER_TYPES[businessType]?.label || textBlank;
};

// 一覧のデータにstoreName列をレンダリングする / render column storeName for list linked data
export const renderStoreName = (item: CooperationInfoList) => {
  const { companyCfc, longNm } = item;
  if (!companyCfc && !longNm) return '';
  if (!companyCfc) return longNm;
  const companyCfcText = companyCfc === COMPANY_CFC.SB ? 'SB' : 'YM';
  if (!longNm) return `（${companyCfcText}）`;
  return `（${companyCfcText}）${longNm}`;
};

// 一覧のデータにacceptedDatetime列をレンダリングする / render column acceptedDatetime for list linked data
export const renderAcceptedDatetime = (acceptedDatetime: string) => {
  const dateTime = formatSendTime(acceptedDatetime);
  const [date, time] = dateTime.split(' ');
  if (!date && !time) return '';
  return `${date}
${time}`;
};

export const reCalcFrom = (index: number, rowPerPage: number = DEFAULT_PAGE_VIEW.RECORD_ROWS) => {
  const halfPageCount = DEFAULT_SCROLL_INFO.SIZE / 2 / rowPerPage;
  const calcStartIndex = index - halfPageCount - 1 < 0 ? 0 : index - halfPageCount - 1;
  return calcStartIndex * rowPerPage;
};

export const labelButtonSort = (order: string) => {
  return order === SORT_TYPE.CREATE_DATE_DESC ? SORT_LABEL_LINKED.CREATE_DATE_ASC : SORT_LABEL_LINKED.CREATE_DATE_DESC;
};

// ログインユーザーから代理店のコード一覧を取得する / get list agency codes from login user
export const getAgencyCodes = (loginUser: CrewUserState) => {
  const { browsableStores, combiAgencyCd } = loginUser;
  const agencyCd = browsableStores ? [...browsableStores] : [];
  if (browsableStores && combiAgencyCd) {
    agencyCd.push(combiAgencyCd);
  }
  return agencyCd;
};

export const renderTextLinkedDataTd = (text: string | null) => {
  return text || '------';
};

export const areCrewCodeAndNameMissing = (registrationCrewCode: string, registrationCrewName: string | null): boolean => {
  const newRegistrationCrewCode = (registrationCrewCode || '').trim();
  const newRegistrationCrewName = (registrationCrewName || '').trim();
  return !newRegistrationCrewCode && !newRegistrationCrewName;
};

export const scrollToTop = (scrollRef: RefObject<HTMLDivElement>) => {
  if (!scrollRef.current) return;
  scrollRef.current.scrollTop = 0;
};
